import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { SohoHeaderComponent, SohoTabsComponent } from 'ids-enterprise-ng';
import { LMBannerHeaderComponentRef } from './lm-banner-header-component-ref.service';
import { LMBannerService } from './lm-banner.service';
import { LMToolbarComponent } from '@toolbar/lm-toolbar.component';
import { LMGlobalStateService } from '@states/services/lm-global-state.service';
import { LMGlobalPersonalizationService } from '@states/services/lm-global-personalization.service';
import { LMGlobalState } from '@states/models/lm-global-state.model';
import { LMGlobalPersonalization } from '@states/models/lm-global-personalization.model';
import { LMHarnessService } from '@services/lm-harness.service';
import { Observable } from 'rxjs';
import { LMColorId } from './lm-theme.service';
import { LMContextService, LMRouteComponentService } from '@services/index';
import { LMBaseComponent } from '@base/lm-base.component';
import { AppConstants } from '@utils/app.constants';

@Component({
  selector: 'lm-banner-header',
  templateUrl: './lm-banner-header.component.html',
  styleUrls:   [ 'lm-banner-header.component.scss' ],
  providers: [ LMBannerService ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LMBannerHeaderComponent implements AfterViewChecked {
  @HostListener('click', [ '$event' ]) onClick(event) {
    if ((event.ctrlKey || event.metaKey) && event.shiftKey) {
      event.stopPropagation();
      const currComp: Component = this.currentComponent.getRouteComponent();
      if (currComp instanceof LMBaseComponent) {
        const baseComp: LMBaseComponent = currComp as LMBaseComponent;
        baseComp.displayComponentInfo(event, baseComp);
      }
    }
  }

  @HostBinding('class.header') get header() {
    return true;
  };
  @HostBinding('class.is-personalizable') get isPersonalizable() {
    return true;
  };
  @HostBinding('class.has-toolbar') get hasToolbar() {
    return true;
  };
  @HostBinding('class.xiHarness') get isJustTools() {
    return !this.m_isXiHarness;
  };
  @HostBinding('class.as-of-date') get hasAsOfDate() {
    return this.asOfDate !== undefined && this.asOfDate !== '';
  };
  @HostBinding('class.db-session-debug-mode') get hasDBSessionDebugMode() {
    return this.lmContextService.hasDBSessionDebugMode();
  };
  @HostBinding('class.sec-session-debug-mode') get isSecSessionDebugMode() {
    return this.lmContextService.isSecSessionDebugMode();
  };
  @HostBinding('attr.id') get elementId(): string {
    return 'app_Header';
  }

  @ViewChild(LMToolbarComponent, { static: true }) lmToolbarComponent: LMToolbarComponent;
  @ViewChild(SohoTabsComponent) sohoTabsComponent: SohoTabsComponent;
  @ViewChild('SohoHeader', { static: true }) sohoHeader: SohoHeaderComponent;

  @Input() set toolbarSectionTitle(toolbarSectionTitle) {
    this.m_toolbarSectionTitle = toolbarSectionTitle;
    this.markForRefresh();
  }
  get toolbarSectionTitle(): string {
    return this.m_toolbarSectionTitle;
  }

  @Input() set toolbarButtons(toolbarButtons: Observable<Array<LMToolbarButton>>) {
    this.m_toolbarButtons = toolbarButtons;
    this.changeDetector.markForCheck();
  }
  get toolbarButtons(): Observable<Array<LMToolbarButton>> {
    return this.m_toolbarButtons;
  }

  @Input() set toolbarSearchField(toolbarSearchField: LMToolbarSearchField) {
    this.m_toolbarSearchField = toolbarSearchField;
    this.changeDetector.markForCheck();
  }

  get toolbarSearchField(): LMToolbarSearchField {
    return this.m_toolbarSearchField;
  }

  @Input() set recordCountString(recordCount: string) {
    this.m_recordCountString = recordCount;
    this.changeDetector.markForCheck();
  }

  get recordCountString(): string {
    return this.m_recordCountString;
  }

  @Input() set hasTabs(hasTabs: boolean) {
    this.m_hasTabs = hasTabs;
    if (hasTabs) {
      this.elementRef.nativeElement.classList.add('has-tabs');
    } else {
      this.elementRef.nativeElement.classList.remove('has-tabs');
    }

    this.hasHeaderTabs.emit(this.m_hasTabs);
  }
  get hasTabs() {
    return this.m_hasTabs;
  }

  @Input() set disabled(disabled: boolean) {
    this.m_disabled = disabled;
    this.markForRefresh();
  }
  get disabled(): boolean {
    return this.m_disabled;
  }

  @Input() set showGoBack(show: boolean) {
    this._showGoBack = show;
    this.markForRefresh();
  }
  get showGoBack(): boolean {
    return this._showGoBack;
  }

  @Input() resizeToolbar: boolean;
  @Input() isListViewDetail: boolean;
  @Input() maxVisibleButtons: number;
  @Input() asOfDate: string;
  @Input() tabsOptions: LMTabsOptions;
  @Input() toolbarMoreSettings: SohoPopupMenuOptions;
  @Input() screenAutomationId: string;

  private _screenId: string;
  @Input() set screenId(screenId: string) {
    this._screenId = screenId;

    if (!this.testVersion1) {
      this.headerTabsetId = screenId + '_tabs';
      this.headerTabAttributes.push({ name: 'id', value: this.headerTabsetId });
    }

    this.headerTabAttributes.push({ name: 'data-automation-id', value: 'lm-' + this.headerTabsetAutomationId + '-tabset' });
  }
  get screenId(): string {
    return this._screenId;
  }

  @Output() tabActivated = new EventEmitter<SohoTabsEvent>();
  @Output() toolbarButtonEvent = new EventEmitter<LMToolbarButtonEvent>();
  @Output() toolbarSearchEvent = new EventEmitter<LMFilterEvent>();
  @Output() toolbarSearchClearedEvent = new EventEmitter<LMFilterEvent>();
  @Output() hasHeaderTabs = new EventEmitter<boolean>();
  @Output() asOfDateChanged: EventEmitter<string> = new EventEmitter<string>();
  @Output() useFullPageScrolling: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() asOfDateCleared: EventEmitter<string> = new EventEmitter();
  @Output() appMenuTriggerEvent: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

  private m_webAppTitle: string;
  private m_hasTabs = false;
  private m_toolbarSectionTitle: string;
  private m_is3_5 = false;
  private m_documentTitleChanged = false;
  private m_toolbarButtons: Observable<Array<LMToolbarButton>>;
  private m_toolbarSearchField = null;
  private m_recordCountString = null;
  private m_disabled = false;
  private m_isXiHarness = false;
  private m_fullPageScroll: boolean;

  private _showGoBack = false;
  private bannerState: LMGlobalState = {};
  private bannerPersonalization: LMGlobalPersonalization = { theme: 'theme-classic-light', color: LMColorId.default };

  public testVersion1: boolean;
  public headerTabAttributes: SohoAttributesArray = [];

  public headerTabsetId: string;
  public headerTabsetAutomationId: string;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private elementRef: ElementRef,
    private titleService: Title,
    private lmBannerHeaderComponentRef: LMBannerHeaderComponentRef,
    private lmContextService: LMContextService,
    private currentComponent: LMRouteComponentService,
    private lmHarnessService: LMHarnessService,
    private lmGlobalStateService: LMGlobalStateService,
    private lmGlobalPersonalizationService: LMGlobalPersonalizationService,
  ) {
    this.lmBannerHeaderComponentRef.instance = this;
    this.loadComponentState();
    this.m_isXiHarness = this.lmHarnessService.isXiHarness();
    this.testVersion1 = this.lmContextService.testVersion() === AppConstants.TESTVERSION_V1;
    this.m_webAppTitle = this.lmContextService.getContext().webAppTitle;
  }

  ngAfterViewChecked() {
    if (!this.m_documentTitleChanged) {
      return;
    }

    if (this.toolbarSectionTitle?.length > 0) { // != null && this.toolbarSectionTitle.length && this.toolbarSectionTitle.length > 0) {
      this.titleService.setTitle(this.toolbarSectionTitle + ' / ' + this.m_webAppTitle);
    } else {
      this.titleService.setTitle(this.m_webAppTitle);
    }
  }

  onToolbarBackButtonEvent() {
    this.lmBannerHeaderComponentRef.popBackReference();
  }

  onAppMenuTriggerEvent(event: MouseEvent) {
    this.appMenuTriggerEvent.emit(event);
  }

  onToolbarButtonEvent(event) {
    this.toolbarButtonEvent.emit(event);
  }

  onToolbarSearchEvent(event) {
    this.toolbarSearchEvent.emit(event);
  }

  onToolbarSearchClearedEvent(event) {
    this.toolbarSearchClearedEvent.emit(event);
  }

  onTabActivated(tabActivatedEvent: any) {
    this.tabActivated.emit(tabActivatedEvent);
  }

  onAsOfDateChangedEvent(date: string) {
    this.asOfDate = date;
    this.asOfDateChanged.emit(date);
  }

  public onAsOfDateCleared(): void {
    this.asOfDateCleared.emit();
  }

  private markForRefresh() {
    this.m_documentTitleChanged = true;

    // ... make sure the change detector kicks in, otherwise if the inputs
    // were change programmatially the component may not be eligible for
    // updating.
    this.changeDetector.markForCheck();
  }

  public set is3_5(is3_5: boolean) {
    this.m_is3_5 = is3_5;
  }

  public get is3_5(): boolean {
    return this.m_is3_5;
  }

  public set fullPageScroll(fullPS: boolean) {
    this.m_fullPageScroll = fullPS;

    this.useFullPageScrolling.emit(fullPS);
  }

  loadComponentState(): void {
    this.bannerState = this.lmGlobalStateService.getGlobalState();
    this.bannerPersonalization = this.lmGlobalPersonalizationService.getGlobalPersonalization();
  }

  saveComponentState(): void {
    this.lmGlobalStateService.saveGlobalState(this.bannerState);
    this.lmGlobalPersonalizationService.saveGlobalPersonalization(this.bannerPersonalization);
  }
}
